import React, { useState, useEffect } from 'react';
import PageLayout from '../../../components/PageLayout/CarelonRx';
import MfaService from '../../../services/mfa/MFAService';
import ContactService from '../../../services/contact/ContactService';
import SupportService from '../../../services/contact/SupportService';
import SecureSessionService from '../../../services/session/SecureSessionService';
import { useFlowNavigation, useFlow } from '../../../contexts/FlowContext';
import { ContactDetails, FactorType, InitiateOtpRequest } from '../../../types/mfa';
import SessionExpiryModal from '../../../components/modals/SessionExpiryModal';
import ReviewDetailsPage from './ReviewDetailsPage';
import '../../../styles/CarelonRx/login.css';
import { ORIGINAL_CLIENT_REDIRECT_URL } from '../../../config';
import { getLogoutRedirectUrl } from '../../../utils/nativeApp';
import '../../../styles/CarelonRx/mfa.css';

const MfaSelectionPage: React.FC = () => {
  const { previousStep, nextStep, goToStep } = useFlowNavigation();
  const { setFlowData, getFlowData, completeStep, currentStep, resetFlow } = useFlow();
  const isNativeApp = getFlowData('device') === 'native_app';
  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<'phone' | 'email' | null>(null);
  const [selectedPhoneId, setSelectedPhoneId] = useState<string>('');
  const [selectedEmailId, setSelectedEmailId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showActionRequiredModal, setShowActionRequiredModal] = useState(false);
  const [supportNumber, setSupportNumber] = useState<string>('');
  const [supportNumberError, setSupportNumberError] = useState<string>('');
  const [apiError, setApiError] = useState(false);
  const [hasCompletedReview, setHasCompletedReview] = useState(false);

  const storedPhoneId = getFlowData('selectedPhoneId');
  const storedEmailId = getFlowData('selectedEmailId');

  const activePhoneId = storedPhoneId || selectedPhoneId;
  const activeEmailId = storedEmailId || selectedEmailId;

  const displayContacts = contactDetails ? {
    phones: activePhoneId
      ? contactDetails.phones.filter(p => p.id === activePhoneId)
      : contactDetails.phones,
    emails: activeEmailId
      ? contactDetails.emails.filter(e => e.id === activeEmailId)
      : contactDetails.emails
  } : null;

  const hasOnlyOneEmail = contactDetails &&
    contactDetails.phones.length === 0 &&
    contactDetails.emails.length === 1;

  const hasOnlyOnePhone = contactDetails &&
    contactDetails.phones.length === 1 &&
    contactDetails.emails.length === 0;

  const hasOnlyEmail = displayContacts &&
    displayContacts.phones.length === 0 &&
    displayContacts.emails.length > 0;


  useEffect(() => {
    const storedSession = SecureSessionService.getStoredSession();
    if (storedSession?.expiresAt) {
      window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt: storedSession.expiresAt } }));
    }

    const initializePage = async () => {
      setSelectedMethod(null);

      await fetchContactDetails();

      const supportContact = getFlowData('supportContact');
      if (supportContact) {
        setSupportNumber(supportContact);
        setSupportNumberError('');
      } else {
        setSupportNumberError("Please call the Pharmacy Member Services number on your member ID card. If you don't have your ID card, please check back later. We are currently experiencing an issue displaying the number and are working to fix it. Pharmacy Member Services is available 24/7.");
      }

      const flowHasCompletedReview = getFlowData('hasCompletedReview');
      if (flowHasCompletedReview) {
        setHasCompletedReview(true);
      }
    };
    initializePage();
  }, []);

  useEffect(() => {
    const hasCompletedReview = getFlowData('hasCompletedReview');

    if (hasOnlyOnePhone && contactDetails && !isLoading && !hasCompletedReview) {
      const phoneId = contactDetails.phones[0].id;
      const phoneNumber = contactDetails.phones[0].maskedNumber;

      setFlowData('selectedPhoneId', phoneId);
      setFlowData('mfaMethod', 'phone');
      setFlowData('mfaContactId', phoneId);
      setFlowData('mfaContactValue', phoneNumber);

      if (currentStep) {
        completeStep(currentStep.id);
      }
      nextStep(); 
    }
  }, [hasOnlyOnePhone, contactDetails, isLoading]);


  useEffect(() => {
    const autoSelect = hasOnlyOneEmail || hasOnlyEmail;
    if (autoSelect && !selectedMethod && contactDetails) {
      setSelectedMethod('email');
      setSelectedPhoneId('');
      const emailId = activeEmailId || contactDetails.emails[0]?.id;
      if (emailId) {
        setSelectedEmailId(emailId);
      }
    }
  }, [hasOnlyOneEmail, hasOnlyEmail, selectedMethod, contactDetails, activeEmailId]);

  const fetchContactDetails = async () => {
    try {
      setIsLoading(true);

      const hadContactApiError = getFlowData('contactApiError');
      if (hadContactApiError) {
        setError('API Error');
        setApiError(true);
        setFlowData('contactApiError', false);
        setIsLoading(false);
        return;
      }

      const cachedDetails = getFlowData('contactDetails');

      if (cachedDetails !== undefined) {
        if (cachedDetails === null) {
          setContactDetails({ phones: [], emails: [] });
        } else {
          setContactDetails(cachedDetails);

          if (!selectedPhoneId && cachedDetails.phones.length > 0) {
            const phoneId = cachedDetails.phones[0].id;
            setSelectedPhoneId(phoneId);
          }
          if (!selectedEmailId && cachedDetails.emails.length > 0) {
            const emailId = cachedDetails.emails[0].id;
            setSelectedEmailId(emailId);
          }
        }

        setIsLoading(false);
        return;
      }

      const session = SecureSessionService.getStoredSession();

      if (!session) {
        setError('Session expired. Please log in again.');
        setIsLoading(false);
        return;
      }

      const details = await MfaService.getContactDetails(session.sessionId);
      setContactDetails(details);
      setFlowData('contactDetails', details);

      if (!selectedPhoneId && details.phones.length > 0) {
        const phoneId = details.phones[0].id;
        setSelectedPhoneId(phoneId);
      }
      if (!selectedEmailId && details.emails.length > 0) {
        const emailId = details.emails[0].id;
        setSelectedEmailId(emailId);
      }

      setIsLoading(false);
    } catch (err: any) {
      const statusCode = err?.response?.status;

      if (statusCode === 404) {
        setError('Contact details not found. Please log in again.');
        setIsLoading(false);
        return;
      }

      if (statusCode === 500 || statusCode === 503 || statusCode === 504) {
      } else {
        const session = SecureSessionService.getStoredSession();
        if (session) {
          try {
            const supportContact = await SupportService.getSupportContactDetails(
              session.sessionId
            );

            if (supportContact) {
              setFlowData('supportContact', supportContact);
              setFlowData('supportContactError', false);
              setSupportNumber(supportContact);
              setSupportNumberError('');
            }
          } catch (supportError) {
            setSupportNumberError('Unable to fetch support contact');
            setFlowData('supportContactError', true);
          }
        }
      }

      setApiError(true);
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (hasOnlyOnePhone || hasOnlyOneEmail) {
      previousStep();
    } else if (hasCompletedReview) {
      setFlowData('hasCompletedReview', false);
      setHasCompletedReview(false);
    } else {
      previousStep();
    }
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };


  const confirmCancel = () => {
    SecureSessionService.clearSession();
    const redirectUrl = isNativeApp
      ? getLogoutRedirectUrl()
      : ORIGINAL_CLIENT_REDIRECT_URL;
    window.location.replace(redirectUrl);
  };

  const dismissCancelModal = () => {
    setShowCancelModal(false);
  };

  const handleNextOnNoContactPage = async () => {
    const session = SecureSessionService.getStoredSession();

    try {
      setIsLoading(true);
      setApiError(false);
      setFlowData('contactApiError', false);

      const session = SecureSessionService.getStoredSession();

      if (!session) {
        setError('Session expired. Please log in again.');
        setIsLoading(false);
        return;
      }
      setFlowData('contactDetails', undefined);

      const details = await MfaService.getContactDetails(session.sessionId);

      if (details && (details.phones.length > 0 || details.emails.length > 0)) {
        setContactDetails(details);
        setFlowData('contactDetails', details);

        if (details.phones.length > 0) {
          setSelectedPhoneId(details.phones[0].id);
        }
        if (details.emails.length > 0) {
          setSelectedEmailId(details.emails[0].id);
        }
      } else {
        setContactDetails({ phones: [], emails: [] });
        setFlowData('contactDetails', null);
        setShowActionRequiredModal(true);
      }
    } catch (err: any) {
      const statusCode = err?.response?.status;

      if (statusCode === 404) {
        setError('Contact details not found. Please try again later.');
        setApiError(true);
      }

      else if (statusCode === 500 || statusCode === 503 || statusCode === 504) {
        setError('Failed to fetch contact details. Please try again.');
        setApiError(true);
      } else {
        setError('Failed to fetch contact details. Please try again.');
        setApiError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    if (!selectedMethod) {
      setError('Please select a verification method');
      return;
    }

    const contactId = selectedMethod === 'phone' ? activePhoneId : activeEmailId;

    if (!contactId) {
      setError('Please select a contact option');
      return;
    }

    try {
      setIsSending(true);
      setError('');

      const session = SecureSessionService.getStoredSession();
      if (!session) {
        setError('Session expired. Please log in again.');
        setIsSending(false);
        return;
      }

      let selectedContactValue = '';

      if (selectedMethod === 'phone') {
        const phoneContact = displayContacts?.phones.find(p => p.id === contactId);
        selectedContactValue = phoneContact?.maskedNumber || '';
      } else {
        const emailContact = displayContacts?.emails.find(e => e.id === contactId);
        selectedContactValue = emailContact?.maskedEmail || '';
      }

      const finalEmailId = activeEmailId || (contactDetails?.emails?.[0]?.id || '');
      const finalPhoneId = activePhoneId || (contactDetails?.phones?.[0]?.id || '');

      setFlowData('selectedEmailId', finalEmailId);
      setFlowData('selectedPhoneId', finalPhoneId);
      setFlowData('mfaMethod', selectedMethod);
      setFlowData('mfaContactId', contactId);
      setFlowData('mfaContactValue', selectedContactValue);

      if (selectedMethod === 'phone') {
        if (currentStep) {
          completeStep(currentStep.id);
        }
        nextStep();
        setIsSending(false);
        return;
      }
      setApiError(false);

      const emailContact = displayContacts?.emails.find(e => e.id === contactId);
      const email = emailContact?.fullEmail || emailContact?.maskedEmail || '';

      const request: InitiateOtpRequest = {
        factorType: 'EMAIL',
        contact: {
          email
        }
      };

      const result = await MfaService.initiateOtp(request, session.sessionId);

      if (result.success) {
        setFlowData('factorId', result.factorId);
        setFlowData('factorType', 'EMAIL');
        setFlowData('selectedContact', email);
        setFlowData('isFactorNewlyEnrolled', result.isFactorNewlyEnrolled);

        if (currentStep) {
          completeStep(currentStep.id);
        }
        goToStep('mfa-verification');
      } else {
        setApiError(true);
      }
    } catch (err) {
      setApiError(true);
    } finally {
      setIsSending(false);
    }
  };

  const handleMethodSelect = (method: 'phone' | 'email') => {
    setSelectedMethod(method);
    setError('');
    setApiError(false);
  };

  if (error) {
    return (
      <ReviewDetailsPage
        error={error}
        supportNumber={supportNumber}
        supportNumberError={supportNumberError}
        onRetry={fetchContactDetails}
        previousStep={previousStep}
        onBack={() => {
          SecureSessionService.clearSession();
          const redirectUrl = isNativeApp
            ? getLogoutRedirectUrl()
            : ORIGINAL_CLIENT_REDIRECT_URL;
          window.location.href = redirectUrl;
        }}
      />
    );
  }

  if (contactDetails &&
    (contactDetails.phones.length > 1 || contactDetails.emails.length > 1) &&
    !hasCompletedReview) {
    return (
      <ReviewDetailsPage
        contactDetails={contactDetails}
        supportNumber={supportNumber}
        supportNumberError={supportNumberError}
        onConfirm={(phoneId, emailId) => {
          setSelectedPhoneId(phoneId);
          setSelectedEmailId(emailId);
          setFlowData('selectedPhoneId', phoneId);
          setFlowData('selectedEmailId', emailId);
          setFlowData('contactDetails', contactDetails);
          setFlowData('hasCompletedReview', true);
          setHasCompletedReview(true);

          const hasMultiplePhones = contactDetails.phones.length > 1;
          const hasMultipleEmails = contactDetails.emails.length > 1;
          const hasPhones = contactDetails.phones.length > 0;
          const hasEmails = contactDetails.emails.length > 0;

          if (phoneId && !hasEmails && hasMultiplePhones) {
            const phoneNumber = contactDetails.phones.find(p => p.id === phoneId)?.maskedNumber;
            setFlowData('mfaMethod', 'phone');
            setFlowData('mfaContactId', phoneId);
            setFlowData('mfaContactValue', phoneNumber);

            if (currentStep) {
              completeStep(currentStep.id);
            }
            nextStep();
          }
          // else {
          //   if (phoneId) {
          //     setSelectedMethod('phone');
          //   } else if (emailId) {
          //     setSelectedMethod('email');
          //   }
          // }
        }}
        previousStep={previousStep}
        onBack={() => {
          SecureSessionService.clearSession();
          const redirectUrl = isNativeApp
            ? getLogoutRedirectUrl()
            : ORIGINAL_CLIENT_REDIRECT_URL;
          window.location.href = redirectUrl;
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <PageLayout bannerTitle="Security Verification for Login">
        <div className="login-page-container">
          <div className="login-left-panel">
            <div className="mfa-loading">
              <div className="loading"></div>
              <p>Loading contact details...</p>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!error && contactDetails && (contactDetails.phones.length === 0 && contactDetails.emails.length === 0)) {
    return (
      <PageLayout bannerTitle="Action Required">
        <div className="login-page-container">
          <div className="login-left-panel">

            {!isNativeApp && (
              <a className="back-link mfa-back-link" onClick={handleBack}>
                <img src="/assets/carelonRx/images/Keyboard Arrow Left.svg" alt="" />
                <span>Back</span>
              </a>
            )}

            <h1 className="mfa-title mfa-no-contact-title">
              Important: Action Required to Log In
            </h1>

            <p className="mfa-subtitle mfa-no-contact-subtitle">
              You must call Pharmacy Member Services to add your phone number and/or email address to your account.
            </p>

            <p className="mfa-subtitle mfa-no-contact-subtitle-secondary">
              Calling is the most secure way to make this change and helps us keep your
              account safe. Once your contact details are added, you will be able to log
              in.
            </p>

            {supportNumberError && (
              <div className="mfa-inline-error">
                <img src="/assets/carelonRx/images/infoIconRed.svg" alt="Error" className="mfa-error-icon" />
                <span>{supportNumberError}</span>
              </div>
            )}

            {!supportNumberError && (
              <div className="mfa-contact-info-card">
                <div className="mfa-contact-card-header">
                  Pharmacy Member Services:
                </div>

                <div className="mfa-contact-card-item">
                  <img src="/assets/carelonRx/images/phone.svg" alt="Phone" className="mfa-contact-icon" />
                  <div className="mfa-contact-card-content">
                    <span className="mfa-contact-label">Call:</span>
                    <a href={`tel:${supportNumber}`} className="mfa-contact-phone-link">
                      {supportNumber}
                    </a>
                  </div>
                </div>

                <div className="mfa-contact-card-item">
                  <img src="/assets/carelonRx/images/timer2.svg" alt="Hours" className="mfa-contact-icon" />
                  <div className="mfa-contact-card-content">
                    <span className="mfa-contact-label">Hours:</span>
                    <span className="mfa-contact-value">24/7</span>
                  </div>
                </div>
              </div>
            )}

            {showActionRequiredModal && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h2 className="modal-title">Important: Action Required to Log In</h2>
                  <p className="modal-message">
                    {supportNumber && !supportNumberError ? (
                      <>
                        You must call Pharmacy Member Services at <a href={`tel:${supportNumber}`} className="mfa-contact-phone-link">{supportNumber}</a> to add your contact details to your account. Calling is the most secure way to do this. Once added, you will be able to log in. Pharmacy Member Services is available 24 hours a day, seven days a week.
                      </>
                    ) : (
                      <>
                        Please call the Pharmacy Member Services number on your member ID card. If you don’t have your ID card, please check back later. We are currently experiencing an issue displaying the number and are working to fix it. Pharmacy Member Services is available 24/7.
                      </>
                    )}
                  </p>
                  <div className="modal-actions">
                    <button onClick={() => setShowActionRequiredModal(false)} className="modal-btn-cancel">
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showCancelModal && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h2 className="modal-title">Are you sure you want to cancel?</h2>
                  <p className="modal-message">
                    Doing so will prevent you from receiving a security verification code, which is necessary to log in.
                  </p>
                  <div className="modal-actions">
                    <button onClick={dismissCancelModal} className="modal-btn-cancel">
                      No, Don't Cancel
                    </button>
                    <button onClick={confirmCancel} className="modal-btn-stay">
                      Yes, Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer with Buttons */}
          <div className="mfa-footer">
            <div className="mfa-footer-buttons">
              <button onClick={handleCancel} className="crx-button crx-button-secondary">
                Cancel
              </button>
              <button onClick={handleNextOnNoContactPage} disabled={isLoading} className="crx-button crx-button-primary">
                {isLoading ? 'Loading...' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout bannerTitle="Security Verification for Login">
      <div className="login-page-container">
        <div className="login-left-panel">

          {!isNativeApp && (
            <a className="back-link mfa-back-link" onClick={handleBack}>
              <img src="/assets/carelonRx/images/Keyboard Arrow Left.svg" alt="" />
              <span>Back</span>
            </a>
          )}

          {!error && (
            <>
              <h1 className={`mfa-title ${hasOnlyEmail ? 'single-email-title' : ''}`}>To ensure your account's security, we will send you a verification code.</h1>
              {!hasOnlyEmail && (
                <p className="mfa-subtitle">
                  Choose how you would like to receive your code:
                </p>
              )}
            </>
          )}

          {!error && (
            <div className="mfa-methods">

              {displayContacts && displayContacts.phones.length > 0 && (
                <div
                  className={`mfa-method-option ${selectedMethod === 'phone' ? 'selected' : ''}`}
                  onClick={() => handleMethodSelect('phone')}
                >
                  <div className="mfa-method-header">
                    <img
                      src={selectedMethod === 'phone'
                        ? "/assets/carelonRx/images/Selectors_clicked.svg"
                        : "/assets/carelonRx/images/Selectors.svg"
                      }
                      alt=""
                      className="mfa-selector-icon"
                    />
                    <div className="mfa-icon-content-wrapper">
                      <img src="/assets/carelonRx/images/phone_iphone.svg" alt="Phone" className="mfa-method-icon" />
                      <div className="mfa-method-content">
                        <div className="mfa-method-label">Phone</div>
                        <div className="mfa-contact-single">
                          {(() => {
                            const phoneIdToDisplay = activePhoneId;
                            const foundPhone = contactDetails?.phones.find(p => p.id === phoneIdToDisplay);
                            const displayPhone = foundPhone?.maskedNumber || displayContacts?.phones?.[0]?.maskedNumber;
                            return displayPhone;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {displayContacts && displayContacts.emails.length > 0 && (
                <div
                  className={`mfa-method-option ${hasOnlyEmail ? 'single-option selected' : selectedMethod === 'email' ? 'selected' : ''
                    }`}
                  onClick={() => !hasOnlyEmail && handleMethodSelect('email')}
                >
                  <div className="mfa-method-header">
                    {!hasOnlyEmail && (
                      <img
                        src={selectedMethod === 'email'
                          ? "/assets/carelonRx/images/Selectors_clicked.svg"
                          : "/assets/carelonRx/images/Selectors.svg"
                        }
                        alt=""
                        className="mfa-selector-icon"
                      />
                    )}
                    <div className="mfa-icon-content-wrapper">
                      <img src="/assets/carelonRx/images/mail.svg" alt="Email" className="mfa-method-icon" />
                      <div className="mfa-method-content">
                        <div className="mfa-method-label">Email</div>
                        <div className="mfa-contact-single">
                          {(() => {
                            const emailIdToDisplay = activeEmailId;
                            const foundEmail = contactDetails?.emails.find(e => e.id === emailIdToDisplay);
                            const displayEmail = foundEmail?.maskedEmail || displayContacts?.emails?.[0]?.maskedEmail;
                            return displayEmail;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="disclaimer-section">
            {selectedMethod === 'email' && (
              <div className="disclaimers" id="mfa-email-info">
                <img src="/assets/carelonRx/images/info.svg" alt="" className="mfa-info-icon" />
                <div className="mfa-disclaimer-text">
                  You'll receive an email with your code from <strong>noreply@carelon.com </strong>with the subject,<strong>"Your CarelonRx Verification Code."</strong>  If you don't see the email in a few minutes, please check your spam folder.
                </div>
              </div>
            )}
            <div className="disclaimers" id="mfa-info-disclaimer">
              {supportNumberError ? (
                <>
                  <img src="/assets/carelonRx/images/infoIconRed.svg" alt="Error" className="mfa-info-icon" />
                  <div className="mfa-disclaimer-text">
                    If your contact details are incorrect, please call the Pharmacy Member Services number on your member ID card. If you don't have your ID card, please check back later. We are currently experiencing an issue displaying the number and are working to fix it. Pharmacy Member Services is available 24/7.
                  </div>
                </>
              ) : (
                <>
                  <img src="/assets/carelonRx/images/info.svg" alt="" className="mfa-info-icon" />
                  <div className="mfa-disclaimer-text">
                    If we don't have the correct contact details to send your verification code, please contact Pharmacy Member Services <a href={`tel:${supportNumber}`} className="mfa-contact-phone-link">{supportNumber}</a> (TTY: 711) for assistance 24 hours a day, 7 days a week.
                  </div>
                </>
              )}
            </div>
            {apiError && (
              <div className="disclaimers" id="mfa-api-error">
                <img src="/assets/carelonRx/images/infoIconRed.svg" alt="Error" className="mfa-info-icon" />
                <div className="mfa-api-disclaimer-text">
                  Sorry, we’re experiencing technical difficulties and can’t send your verification code. Please try again later. If the issue continues, please call our Technical Support team at 1-844-430-0335 (TTY/TDD 711), Monday through Friday, from 8 a.m. to 8 p.m. ET, to repeat the issue.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mfa-footer">
          <div className="mfa-footer-buttons">
            <button
              onClick={handleCancel}
              className="crx-button crx-button-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleNext}
              disabled={!!error || isSending || !selectedMethod}
              className="crx-button crx-button-primary"
            >
              {isSending ? 'Sending...' : selectedMethod === 'email' ? 'Send My Code' : 'Next'}
            </button>
          </div>
        </div>

        {showCancelModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Are you sure you want to cancel?</h2>
              <p className="modal-message">
                Doing so will prevent you from receiving a security verification code, which is necessary to log in.
              </p>
              <div className="modal-actions">
                <button onClick={dismissCancelModal} className="modal-btn-cancel">
                  No, Don't Cancel
                </button>
                <button onClick={confirmCancel} className="modal-btn-stay">
                  Yes, Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <SessionExpiryModal />
    </PageLayout>
  );
};

export default MfaSelectionPage;

